/**
 * `panel-views` — pure render-functies voor header / body / footer van het
 * chat-paneel. Eén `switch (layout.view)` per slot: header en body en footer
 * kijken naar dezelfde `PanelView`-enum (bron: `derivePanelLayout`) zodat ze
 * niet uit elkaar kunnen drift'en.
 *
 * ## Waarom losstaand van `<embrace-chat>`
 *
 * De render-routing was 225 LoC binnen de root-element-class — de class
 * werd onleesbaar lang. Door deze drie functies pure te maken (input:
 * `PanelLayout + PanelViewDeps`, output: `TemplateResult | typeof nothing`):
 *
 *   1. `<embrace-chat>` blijft thin: launcher + paneel-frame + event-routing.
 *   2. De render-keuzes zijn unit-testbaar zonder Lit-host op te tuigen —
 *      gewoon een fake `ChatState` + fake handlers en assert op het output-
 *      template.
 *
 * ## PanelViewDeps
 *
 * Flat object met state, config-waardes en event-handlers. Bewust géén
 * `host: EmbraceChat`-pointer: dat zou de views koppelen aan de hele
 * host-surface en testen onmogelijk maken zonder LitElement.
 *
 * ## Side-effect imports
 *
 * De `import './components/foo';` regels registreren custom-elements
 * (`@customElement(...)`-decorator). Ze leven hier omdat dit de file is
 * die de tags daadwerkelijk gebruikt — wie deze module importeert krijgt
 * de elementen mee.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { i18n } from '../i18n/nl';
import {
  canDownloadTranscript,
  selectChatEstablished,
  selectRatingUi,
  type PanelLayout,
} from '../state/selectors';
import type { AttachedFile, ChatState } from '../state/types';
import type {
  ChannelId,
  ChannelOverrides,
} from '../components/channel-picker';
import '../components/chat-header';
import type { HeaderMode } from '../components/chat-header';
import '../components/message-list';
import '../components/chat-composer';
import '../components/intake-form';
import '../components/status-hero';
import '../components/ended-banner';
import '../components/channel-picker';
import '../components/external-channel';
import '../components/searching-banner';
import '../components/auth-required';
import '../components/chat-captcha';

export type PanelViewDeps = {
  state: ChatState;
  /** Organisatie-naam (header-titel). */
  orgName: string;
  /** Fallback-contact bij offline-state. */
  contactEmail: string;
  /** WhatsApp-nummer voor external-channel-view. */
  whatsappNumber: string;
  /** Messenger-paginanaam voor external-channel-view. */
  messengerPage: string;
  /** Spoednummer voor de AI-gebruiksvoorwaarden (`[spoednummer]`). Leeg = fallback. */
  repairEmergencyNumber: string;
  /** Support-kanaal voor de AI-gebruiksvoorwaarden (`[kanaal/livechat]`). Leeg = fallback. */
  repairSupportChannel: string;
  /** Of de emoji-knop in de composer beschikbaar is (config `enableEmoji`). */
  enableEmoji: boolean;
  /** Optionele per-kanaal overrides (naam, blurb, kleuren, SLA, logo). */
  channelOverrides?: ChannelOverrides;
  /**
   * Optionele inlog-URL voor de auth-required-view. Wanneer gezet, krijgt
   * de bezoeker een directe "Inloggen"-knop; anders alleen "Terug".
   */
  repairLoginUrl?: string;

  // Event-handlers — host bindt deze aan zijn eigen dispatch-helpers.
  onClosePanel: () => void;
  onBackToChannelPicker: () => void;
  onDownloadTranscript: () => void;
  onIntakeSubmit: (
    e: CustomEvent<{ question: string; captchaToken: string }>,
  ) => void;
  onUserSubmit: (e: CustomEvent<{ text: string }>) => void;
  onOpenFilePicker: () => void;
  onRemoveStaged: (e: CustomEvent<{ fileId: string }>) => void;
  onEndConversation: () => void;
  onStartNewConversation: () => void;
  onPickChannel: (e: CustomEvent<{ channel: ChannelId }>) => void;
  /** Bezoeker klikt op "Chatten met medewerker" in de gegroepeerde picker. */
  onPickerDrilldownChat: () => void;
  /** Bezoeker klikt back-button in de chat-list sub-view van de picker. */
  onPickerBackToRoot: () => void;
  onRateSubmit: (e: CustomEvent<{ rating: number }>) => void;
  onVisitorForget: () => void;
  /** Klik op "Opnieuw verzenden" bij een failed-customer-message. */
  onRetryMessage: (e: CustomEvent<{ localId: string }>) => void;
  /** Bezoeker klikt een contract-card in de inline AVAA-keuze. */
  onContractSelect: (e: CustomEvent<{ contractId: string }>) => void;
  /** Bezoeker klikt "Opnieuw proberen" na een mislukte contract-fetch. */
  onContractRetry: () => void;
  /** Self-fix "Ja, opgelost" — chat sluiten. */
  onSelfFixConfirm: () => void;
  /** Self-fix "Nee, niet opgelost" — excuus + redirect naar de RR-pagina. */
  onSelfFixDecline: () => void;
  /** POC conversational intake: Turnstile-callback levert de captcha-token. */
  onCaptchaVerified: (e: CustomEvent<{ token: string }>) => void;
};

// ===========================================================================
// Header
// ===========================================================================

export function renderHeader(
  layout: PanelLayout,
  deps: PanelViewDeps,
): TemplateResult {
  const { state: s, orgName, onClosePanel, onBackToChannelPicker, onDownloadTranscript } = deps;
  const showBack = layout.showBackButton;
  const canDownload = canDownloadTranscript(s);
  // "Chatgesprek sluiten" zichtbaar zodra de bezoeker iets actiefs aan
  // het doen is: bot-Q&A (asking-name/asking-email/searching), session-
  // request, queue of actief gesprek. Niet voor ended (al gesloten) en
  // niet voor lege intake-form (nog niets om te sluiten — het paneel-X
  // volstaat). Tijdens bot-flow doet "sluiten" een SESSION_RESET; tijdens
  // queued/active een echte SESSION_ENDED met /leave-call.
  const canClose =
    s.session.phase === 'queued' ||
    s.session.phase === 'active' ||
    s.session.phase === 'requesting' ||
    (s.session.phase === 'none' && s.assistant !== null);
  // Actions-menu wordt getoond op intake / assistant / queued / active /
  // ended phases wanneer er ten minste één actie van toepassing is.
  // Channel-picker / external-channel / offline / bootstrap krijgen 'm
  // niet — geen relevante context.
  const hasVisitor = s.visitor !== null && s.visitor.email !== '';
  const inMenuPhase =
    layout.view === 'intake-form' ||
    layout.view === 'assistant-flow' ||
    layout.view === 'queued-or-requesting' ||
    layout.view === 'active-or-ended';
  const showSettings =
    inMenuPhase && (hasVisitor || canDownload || canClose);

  switch (layout.view) {
    case 'bootstrap':
      return html`
        <embrace-chat-header
          slot="header"
          mode="hero"
          .orgName=${orgName}
          @close-panel=${onClosePanel}
        ></embrace-chat-header>
      `;
    case 'channel-picker': {
      const inDrilldown = s.ui.pickerStep === 'chat-list';
      return html`
        <embrace-chat-header
          slot="header"
          mode="hero"
          .orgName=${orgName}
          .showBack=${inDrilldown}
          @close-panel=${onClosePanel}
          @back=${deps.onPickerBackToRoot}
        ></embrace-chat-header>
      `;
    }
    case 'external-channel':
      return html`
        <embrace-chat-header
          slot="header"
          mode="intake"
          .orgName=${orgName}
          .showBack=${showBack}
          @close-panel=${onClosePanel}
          @back=${onBackToChannelPicker}
        ></embrace-chat-header>
      `;
    case 'auth-required':
      return html`
        <embrace-chat-header
          slot="header"
          mode="auth-required"
          .orgName=${orgName}
          .showBack=${showBack}
          @close-panel=${onClosePanel}
          @back=${onBackToChannelPicker}
        ></embrace-chat-header>
      `;
    case 'identifying': {
      // D3-skeleton: header alvast in de bestemmings-mode zodat de swap
      // naar de echte view (intake-form of auth-required) geen header-
      // re-mount oplevert. `repair-request`-pick → auth-required-header,
      // alle andere paden (`webchat`, geen pick) → intake-header.
      const headerMode: HeaderMode =
        s.ui.pickedChannel === 'repair-request' ? 'auth-required' : 'intake';
      return html`
        <embrace-chat-header
          slot="header"
          .mode=${headerMode}
          .orgName=${orgName}
          .showBack=${showBack}
          @close-panel=${onClosePanel}
          @back=${onBackToChannelPicker}
        ></embrace-chat-header>
      `;
    }
    case 'intake-form':
      return html`
        <embrace-chat-header
          slot="header"
          mode="intake"
          .orgName=${orgName}
          .showBack=${showBack}
          .showSettingsMenu=${showSettings}
          .canDownloadTranscript=${canDownload}
          .canCloseConversation=${canClose}
          .visitorFirstName=${s.visitor?.firstName ?? ''}
          .visitorEmail=${s.visitor?.email ?? ''}
          @close-panel=${onClosePanel}
          @back=${onBackToChannelPicker}
          @visitor-forget=${deps.onVisitorForget}
          @download-transcript=${onDownloadTranscript}
          @end-conversation=${deps.onEndConversation}
        ></embrace-chat-header>
      `;
    case 'service-offline':
      return html`
        <embrace-chat-header
          slot="header"
          mode="closed"
          .orgName=${orgName}
          .showBack=${showBack}
          @close-panel=${onClosePanel}
          @back=${onBackToChannelPicker}
        ></embrace-chat-header>
      `;
    case 'assistant-flow':
    case 'queued-or-requesting':
    case 'active-or-ended': {
      // Eén html`…`-call gedeeld over alle in-conversation-views: Lit
      // hergebruikt DOM alleen als het template-literal identiek is.
      // Aparte case-takken zouden de header per view-switch unmounten
      // en de avatar laten flikkeren.
      const phase = s.session.phase;
      const operator =
        phase === 'active' || phase === 'ended' ? s.session.operator : null;
      const mode =
        phase === 'ended' ? 'ended' : phase === 'active' ? 'agent' : 'assistant';
      // Transcript-download alleen relevant zodra er server-messages zijn.
      const canDownloadInMenu = layout.view !== 'assistant-flow' && canDownload;
      return html`
        <embrace-chat-header
          slot="header"
          mode=${mode}
          .operator=${operator}
          .orgName=${orgName}
          .showSettingsMenu=${showSettings}
          .canDownloadTranscript=${canDownloadInMenu}
          .canCloseConversation=${canClose}
          .visitorFirstName=${s.visitor?.firstName ?? ''}
          .visitorEmail=${s.visitor?.email ?? ''}
          @close-panel=${onClosePanel}
          @download-transcript=${onDownloadTranscript}
          @visitor-forget=${deps.onVisitorForget}
          @end-conversation=${deps.onEndConversation}
        ></embrace-chat-header>
      `;
    }
  }
}

// ===========================================================================
// Body
// ===========================================================================

export function renderBody(
  layout: PanelLayout,
  deps: PanelViewDeps,
): TemplateResult {
  const {
    state: s,
    contactEmail,
    whatsappNumber,
    messengerPage,
    onPickChannel,
    onIntakeSubmit,
  } = deps;

  switch (layout.view) {
    case 'bootstrap':
      return html`<div class="bootstrap">${i18n.misc.bootstrap}</div>`;
    case 'channel-picker':
      return html`
        <embrace-channel-picker
          .channels=${layout.channels}
          .step=${s.ui.pickerStep}
          .webchatOnline=${s.serviceStatus?.isAcceptingConversationRequests ?? false}
          .channelOverrides=${deps.channelOverrides}
          @pick-channel=${onPickChannel}
          @drilldown-chat=${deps.onPickerDrilldownChat}
        ></embrace-channel-picker>
      `;
    case 'external-channel':
      return html`
        <embrace-external-channel
          .channel=${s.ui.pickedChannel}
          .whatsappNumber=${whatsappNumber}
          .messengerPage=${messengerPage}
          .channelOverrides=${deps.channelOverrides}
        ></embrace-external-channel>
      `;
    case 'auth-required':
      return html`
        <embrace-auth-required
          .loginUrl=${deps.repairLoginUrl ?? ''}
          @back-to-picker=${deps.onBackToChannelPicker}
        ></embrace-auth-required>
      `;
    case 'identifying':
      // D3-skeleton-body: hergebruikt het `.bootstrap`-blok (zelfde
      // styling, zelfde placement) zodat de swap naar de echte view
      // geen layout-shift oplevert. Korte copy zodat de bezoeker niet
      // denkt dat het paneel hangt.
      return html`<div class="bootstrap">${i18n.misc.identifying}</div>`;
    case 'service-offline':
      return html`
        <embrace-status-hero
          mode="offline"
          .openingHours=${s.serviceStatus?.openingHours}
          .todayIndex=${s.serviceStatus?.todayIndex ?? 0}
          .contactEmail=${contactEmail}
        ></embrace-status-hero>
      `;
    case 'intake-form': {
      // Authenticated bezoekers krijgen geen Turnstile: server doet geen
      // captcha-check op /authenticated/new (alleen op /anonymous/new),
      // dus de widget vragen is ruis voor de bezoeker en kost een extra
      // Cloudflare-roundtrip.
      const sitekey =
        s.auth === 'authenticated'
          ? ''
          : s.serviceStatus?.turnstile?.siteKey ?? '';
      return html`
        <embrace-intake-form
          .sitekey=${sitekey}
          .panelOpen=${s.ui.panelOpen}
          @intake-submit=${onIntakeSubmit}
        ></embrace-intake-form>
      `;
    }
    case 'assistant-flow':
    case 'queued-or-requesting':
    case 'active-or-ended': {
      // Gedeelde html`…`-call (zelfde Lit-template = hergebruikte DOM-
      // node). Aparte case-takken zouden de message-list per view-switch
      // unmounten — scroll-positie zou resetten en de bot-typing-state
      // (`revealAt`) zou wissen.
      const phase = s.session.phase;
      const operator =
        phase === 'active' || phase === 'ended' ? s.session.operator : null;
      const sessionEnded = phase === 'ended';
      const endedAt = phase === 'ended' ? s.session.endedAt : 0;
      const endedReason = phase === 'ended' ? s.session.reason : null;
      const ratingUi = selectRatingUi(s);
      const submittedRating =
        phase === 'ended' && s.session.rating?.status === 'submitted'
          ? s.session.rating.rating
          : 0;
      // serverMessages-filter per view:
      //   - assistant-flow  : geen serverMessages (pre-session)
      //   - queued/requesting: alleen customer-echoes; operator-messages
      //                        nog verbergen tot bot-aankondiging
      //   - active/ended    : alle messages
      const serverMessages =
        layout.view === 'queued-or-requesting'
          ? s.serverMessages.filter((m) => m.author === 'customer')
          : layout.view === 'assistant-flow'
            ? []
            : s.serverMessages;
      // pendingMessages alleen wanneer queuen mogelijk is — tijdens
      // asking-name/asking-email gaat input naar ASSISTANT_USER_REPLIED.
      const showPending =
        layout.view !== 'assistant-flow' || s.assistant?.step === 'searching';
      const showContractChoices =
        s.assistant?.step === 'aava-selecting-contract';
      const aavaThinking =
        s.assistant?.step === 'aava-asking' &&
        (s.aava.status === 'starting' || s.aava.status === 'sending');
      // AI-gebruiksvoorwaarden bovenaan de AAVA-chat (inklapbaar). orgName
      // vervangt [Woningcorporatie]; de configureerbare velden vervangen
      // [spoednummer] en [kanaal/livechat] (lege waarde → nette fallback).
      const isAavaFlow =
        s.assistant?.step === 'aava-selecting-contract' ||
        s.assistant?.step === 'aava-asking';
      const aavaDisclaimer = isAavaFlow
        ? i18n.aava.disclaimerLong(
            deps.orgName,
            deps.repairEmergencyNumber,
            deps.repairSupportChannel,
          )
        : '';
      const awaitingCaptcha = s.assistant?.step === 'awaiting-captcha';
      const turnstileSiteKey = s.serviceStatus?.turnstile?.siteKey ?? '';
      return html`
        <embrace-message-list
          .assistantMessages=${s.assistantMessages}
          .serverMessages=${serverMessages}
          .pendingMessages=${showPending ? s.pendingMessages : []}
          .operator=${operator}
          .agentTyping=${s.agentTyping}
          .sessionEnded=${sessionEnded}
          .sessionEndedAt=${endedAt}
          .sessionEndedReason=${endedReason}
          .ratingVariant=${ratingUi}
          .submittedRating=${submittedRating}
          .showContractChoices=${showContractChoices}
          .contracts=${showContractChoices ? s.contracts : null}
          .showSelfFix=${s.aava.selfFix === 'asking'}
          .aavaThinking=${aavaThinking}
          .aavaDisclaimer=${aavaDisclaimer}
          @rate-submit=${deps.onRateSubmit}
          @retry-message=${deps.onRetryMessage}
          @contract-select=${deps.onContractSelect}
          @contract-retry=${deps.onContractRetry}
          @self-fix-confirm=${deps.onSelfFixConfirm}
          @self-fix-decline=${deps.onSelfFixDecline}
        ></embrace-message-list>
        ${awaitingCaptcha && turnstileSiteKey
          ? html`<embrace-chat-captcha
              .sitekey=${turnstileSiteKey}
              @captcha-verified=${deps.onCaptchaVerified}
            ></embrace-chat-captcha>`
          : null}
      `;
    }
  }
}

// ===========================================================================
// Footer
// ===========================================================================

export function renderFooter(
  layout: PanelLayout,
  deps: PanelViewDeps,
): TemplateResult | typeof nothing {
  const {
    state: s,
    onUserSubmit,
    onDownloadTranscript,
    onStartNewConversation,
  } = deps;
  const maxLen = s.serviceStatus?.maxMessageLength ?? 4000;
  // Emoji- én bijlage-knop pas vrijgeven zodra het gesprek "staat" (WI 164916
  // emoji, WI 164914 bijlage) — vóór dat punt zijn ze zichtbaar-maar-disabled.
  // Eén keer afgeleid, gedeeld door beide composer-takken.
  const established = selectChatEstablished(s);

  switch (layout.view) {
    case 'bootstrap':
    case 'channel-picker':
    case 'external-channel':
    case 'auth-required':
    case 'identifying':
    case 'service-offline':
    case 'intake-form':
      // Geen composer/banner in deze views — body biedt de actie zelf
      // (of bij 'identifying' is er bewust nog niets).
      return nothing;
    case 'assistant-flow': {
      if (s.assistant === null) return nothing; // type-narrowing
      // Self-fix-tak (`outcome: Resolved`): geen composer meer. Dekt zowel de
      // openstaande ja/nee-vraag als het venster ná "Nee" (excuus + redirect)
      // en het geval dat er geen redirect-URL is — het gesprek is afgerond.
      if (s.aava.outcome === 'Resolved') return nothing;
      // asking-name / asking-email / aava-asking: bot vraagt actief om
      // input → composer. searching: bot wacht op een medewerker →
      // searching-banner vervangt de composer. aava-selecting-contract:
      // composer disabled + hint, bezoeker kiest inline via cards.
      if (s.assistant.step === 'searching') {
        return html`<embrace-searching-banner slot="footer"></embrace-searching-banner>`;
      }
      const aavaBusy =
        s.assistant.step === 'aava-asking' &&
        (s.aava.status === 'starting' || s.aava.status === 'sending');
      // step → composer-disabled-hint. `aava-asking` is alleen disabled
      // wanneer er een AAVA-call in-flight is — dat zit niet in de step,
      // dus die conditie blijft buiten de map en valt onder `aavaBusy`.
      const stepHints: Partial<Record<NonNullable<typeof s.assistant>['step'], string>> = {
        'aava-selecting-contract': i18n.contracts.selectFirstHint,
        'awaiting-captcha': i18n.composer.disabledHintAwaitingCaptcha,
        'awaiting-identity': i18n.composer.disabledHintAwaitingIdentity,
      };
      const stepHint = stepHints[s.assistant.step];
      const disabledHint = stepHint ?? (aavaBusy ? i18n.aava.replying : '');
      const disabled = disabledHint !== '';
      // AI-disclaimer in dezelfde foot-regio als de "…is aan het denken…"-hint,
      // maar getoond wanneer de composer ENABLED is en de conversation loopt
      // (server heeft een bericht teruggestuurd → conversationId gezet).
      const aavaDisclaimer =
        s.assistant.step === 'aava-asking' && s.aava.conversationId !== null
          ? i18n.aava.disclaimerShort
          : '';
      // Repair-specifieke placeholder alleen in de AAVA-intake; de generieke
      // assistent-flow ("stel je vraag → medewerker") krijgt de neutrale
      // default, anders spreekt de placeholder de begroeting tegen.
      const isAava =
        s.assistant.step === 'aava-selecting-contract' ||
        s.assistant.step === 'aava-asking';
      const placeholder =
        s.assistant.step === 'asking-name'
          ? i18n.composer.placeholderName
          : s.assistant.step === 'asking-email'
            ? i18n.composer.placeholderEmail
            : isAava
              ? i18n.composer.placeholderRepairQuestion
              : i18n.composer.placeholderDefault;
      const composerMode =
        s.assistant.step === 'asking-email' ? 'email' : 'text';
      // Bijlagen alleen tijdens AVAA-asking — andere assistant-flow-stappen
      // (asking-name/email) zijn pure-text Q&A waar files niets toevoegen.
      const isAavaAsking = s.assistant.step === 'aava-asking';
      return html`
        <embrace-chat-composer
          slot="footer"
          placeholder=${placeholder}
          mode=${composerMode}
          maxLength=${maxLen}
          ?disabled=${disabled}
          disabledHint=${disabledHint}
          footnote=${aavaDisclaimer}
          .enableEmoji=${deps.enableEmoji}
          .enableAttach=${isAavaAsking}
          .established=${established}
          .stagedFiles=${isAavaAsking ? s.stagedFiles : ([] as AttachedFile[])}
          @user-submit=${onUserSubmit}
          @open-file-picker=${isAavaAsking ? deps.onOpenFilePicker : undefined}
          @remove-staged=${isAavaAsking ? deps.onRemoveStaged : undefined}
        ></embrace-chat-composer>
      `;
    }
    case 'queued-or-requesting':
    case 'active-or-ended': {
      const phase = s.session.phase;
      if (phase === 'ended') {
        return html`
          <embrace-ended-banner
            slot="footer"
            @download-transcript=${onDownloadTranscript}
            @start-new-conversation=${onStartNewConversation}
          ></embrace-ended-banner>
        `;
      }
      // Beide componenten altijd gemount: pas dan kan de CSS-grid stack
      // de composer onder de searching-banner vandaan laten sliden in
      // plaats van met een harde swap. Animatie-regels in `.footer-stack`
      // (embrace-chat.ts) reageren op data-stage.
      const stage = phase === 'active' ? 'active' : 'searching';
      return html`
        <div class="footer-stack" data-stage=${stage} slot="footer">
          <embrace-searching-banner></embrace-searching-banner>
          <embrace-chat-composer
            placeholder=${i18n.composer.placeholderDefault}
            maxLength=${maxLen}
            .enableEmoji=${deps.enableEmoji}
            .enableAttach=${false}
            .established=${established}
            @user-submit=${onUserSubmit}
          ></embrace-chat-composer>
        </div>
      `;
    }
  }
}
